// ══════════════════════════════════════════════════════════════
// Design Tokens — PencilWise visual system  (JS MIRROR)
// Spec: "Duolingo-like delight outside the problem; workbook-like
// clarity inside the problem." See design brief, May 2026.
//
// ⚠ SINGLE SOURCE OF TRUTH = src/styles/globals.css :root.
// The CSS custom properties there are canonical for the whole app.
// This file MIRRORS the color values as JS string literals because the
// canvas/SVG visuals (TenFrame, NumberLine, MakeTenBridge, …) draw at
// runtime and can't read CSS vars. Every COLORS value below must equal
// its globals.css var — enforced by scripts/audit/tokenParityTest.mjs
// (npm run test:token-parity, in the audit gate). Change a color in
// globals.css first, then sync it here. (Phase 0 reconcile, 2026-06.)
// ══════════════════════════════════════════════════════════════

// ── Color tokens ─────────────────────────────────────────────
// Subject accents follow the brief: math=sky, reading=plum, writing=coral.
// Canvas is intentionally warm-white (not stark white) for a workbook feel.
export const COLORS = {
  // Canvas + text
  bg:        "#FFFDF8",  // warm white canvas
  bgWarm:    "#FFF7E8",  // tinted card backgrounds (mascot, scaffold cards)
  surface:   "#FFFFFF",  // pure white for option chips
  surfaceGlass: "rgba(255,255,255,0.86)",
  text:      "#2A1F1A",  // = globals --ink (Phase 0: warm ink is canonical; was #24303A charcoal)
  textSoft:  "#5C6470",  // = globals --text-soft  (workbook secondary ramp)
  muted:     "#8C95A1",  // = globals --text-muted (workbook helper/labels)
  border:    "rgba(42,31,26,0.10)",
  divider:   "rgba(42,31,26,0.06)",

  // Subject accents (per brief)
  sky:       "#4EA8FF",  // math
  skyLight:  "#E5F2FF",
  skySoft:   "#A8D2FF",

  plum:      "#8B5CF6",  // reading
  plumLight: "#EFE7FF",
  plumSoft:  "#C5AAFB",

  coral:     "#FF7A59",  // writing
  coralLight:"#FFE7DF",
  coralSoft: "#FFB59E",

  // Semantic states (per brief)
  leaf:      "#36C26B",  // success
  leafLight: "#E0F5E8",
  sunflower: "#F5C542",  // hint
  sunflowerLight: "#FFF4D0",
  raspberry: "#E35D6A",  // error
  raspberryLight: "#FCE3E6",

  // Reward chrome
  gold:      "#F5C542",
  goldDeep:  "#D9A82E",

  // Strategy palette — used by math-strategy diagrams (MakeTenBridge,
  // BridgeTenSubtraction, NumberLine annotations) where two operands and
  // a "bridge" need three visually distinct colors. Kept separate from
  // subject accents so the diagram colors don't drift when subject themes
  // change. Pink = first operand, Blue = second operand,
  // Purple = bridge/result, StrategyStroke = outline for diagram chrome.
  strategyPink:   "#FF8AA3",
  strategyBlue:   "#7CB1FF",
  strategyPurple: "#7C4DFF",
  strategyStroke: "#3A3050",

  // Legacy aliases — kept so older components don't break before refactor
  purple:    "#8B5CF6",  // → plum
  purpleLight: "#EFE7FF",
  purpleSoft: "#C5AAFB",
  blue:      "#4EA8FF",  // → sky
  blueLight: "#E5F2FF",
  blueSoft:  "#A8D2FF",
  green:     "#36C26B",  // → leaf
  greenLight:"#E0F5E8",
  red:       "#E35D6A",  // → raspberry
  redLight:  "#FCE3E6",
  goldLight: "#FFF4D0",
  pink:      "#FF7A59",  // → coral
};

// ── Typography ───────────────────────────────────────────────
// Sizes per brief: 32/36 display, 24/28 prompt, 20/24 option, 16/20 helper.
export const TYPE = {
  display:    { size: 32, line: 36, weight: 900, letter: -0.5 },
  displaySm:  { size: 26, line: 30, weight: 900, letter: -0.3 },
  prompt:     { size: 24, line: 28, weight: 800, letter: 0 },
  promptLg:   { size: 32, line: 36, weight: 900, letter: -0.3 },
  option:     { size: 20, line: 24, weight: 800, letter: 0 },
  optionLg:   { size: 24, line: 28, weight: 800, letter: 0 },
  body:       { size: 16, line: 22, weight: 600, letter: 0 },
  helper:     { size: 14, line: 18, weight: 700, letter: 0.2 },
  micro:      { size: 12, line: 16, weight: 800, letter: 0.4 },
  // h1/h2/h3 aliases used by WhosPlayingScreen, DashboardScreen, etc.
  // Without these the screens read TYPE.h3.size on undefined and crash
  // with "Cannot read properties of undefined (reading 'size')."
  h1:         { size: 32, line: 36, weight: 900, letter: -0.5 },
  h2:         { size: 26, line: 30, weight: 900, letter: -0.3 },
  h3:         { size: 20, line: 24, weight: 800, letter: 0 },
  // Family stack — system fonts fall back gracefully on iPad/Android
  fontFamily: '"Nunito", "SF Pro Rounded", "Segoe UI", system-ui, -apple-system, sans-serif',
};

// ── Spacing scale (8pt grid) ─────────────────────────────────
export const SPACE = { 0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 40, 9: 48, 10: 64 };

// ── Radius scale ─────────────────────────────────────────────
export const RADIUS = { sm: 8, md: 12, lg: 16, xl: 22, pill: 999 };

// ── Elevation (subtle, not dramatic) ─────────────────────────
export const SHADOW = {
  card:  "0 4px 20px rgba(42,31,26,0.06)",
  chip:  "0 2px 8px rgba(42,31,26,0.06)",
  raised:"0 8px 24px rgba(42,31,26,0.10)",
  scaffold: "0 4px 20px rgba(80,80,140,0.06)",  // for in-problem visual cards
};

// ── Scaffold card primitive tokens ───────────────────────────
// Every assessment visual that wraps its content in a card (TenFrame,
// MakeTenBridge, BridgeTenSubtraction, ComparisonLadder, syllable_count,
// shape_recognize, etc.) reads these tokens so the system-wide visual
// language stays consistent. 3b extracts a `<ScaffoldCard>` component
// that bundles them; this is the underlying token layer.
export const SCAFFOLD = {
  bg:           "#FFF7E8",  // = COLORS.bgWarm; declared separately so the
                            //   scaffold layer doesn't depend on COLORS for
                            //   tree-shake reasons in some downstream paths
  radius:       22,
  radiusSm:     14,         // for inner cells / inputs inside a scaffold card
  padding:      16,
  paddingX:     20,
  borderColor:  "rgba(42,31,26,0.10)",
  shadow:       "0 4px 20px rgba(80,80,140,0.06)",
};

// ── Visual coordinates kept for legacy SVG components ─────────
export const VISUAL = {
  canvas: { width: 360, height: 220 },
  hitTarget: { min: 44 },
  dot: { radius: 14, strokeWidth: 2 },
  tenFrame:  { x: 50, y: 35, cols: 5, rows: 2, cellW: 44, cellH: 44, gap: 4 },
  fiveFrame: { x: 70, y: 40, cells: 5, cellW: 44, cellH: 44, gap: 4 },
  numberBond:{ wholePos: [180, 160], partPos: [[125, 105], [235, 105]], radius: 28 },
  numberLine:{ x1: 40, x2: 320, y: 165, tickCount: 11 },
};
